import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

/**
 * AdaptiveFilters - Dynamic filter chips based on section's frequent tags/colors
 * 
 * Props:
 * - items: Array of items to analyze for frequent tags/colors
 * - maxFilters: Maximum number of filter chips (default: 8)
 * - onFiltersChange: Callback when filters change
 */
export default function AdaptiveFilters({ items, maxFilters = 8, onFiltersChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    tags: [],
    colors: [],
  });

  useEffect(() => {
    // Extract current filters from URL
    const tags = searchParams.get('tags')?.split(',').filter(Boolean) || [];
    const colors = searchParams.get('colors')?.split(',').filter(Boolean) || [];
    
    setFilters({ tags, colors });
  }, [searchParams]);

  useEffect(() => {
    if (!items || items.length === 0) return;

    // Analyze items for frequent tags/colors
    const tagCounts = {};
    const colorCounts = {};

    items.forEach(item => {
      (item.tags || []).forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
      (item.colors || []).forEach(color => {
        colorCounts[color] = (colorCounts[color] || 0) + 1;
      });
    });

    // Get top tags and colors
    const topTags = Object.entries(tagCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, Math.floor(maxFilters * 0.6)) // 60% tags
      .map(([tag]) => tag);

    const topColors = Object.entries(colorCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, Math.floor(maxFilters * 0.4)) // 40% colors
      .map(([color]) => color);

    // Only update if we have new suggestions and current filters are empty
    if (filters.tags.length === 0 && filters.colors.length === 0) {
      // Don't auto-apply, just show suggestions
    }
  }, [items, maxFilters, filters.tags.length, filters.colors.length]);

  const toggleFilter = (type, value) => {
    const newFilters = { ...filters };
    
    if (type === 'tag') {
      if (newFilters.tags.includes(value)) {
        newFilters.tags = newFilters.tags.filter(t => t !== value);
      } else {
        newFilters.tags = [...newFilters.tags, value];
      }
    } else if (type === 'color') {
      if (newFilters.colors.includes(value)) {
        newFilters.colors = newFilters.colors.filter(c => c !== value);
      } else {
        newFilters.colors = [...newFilters.colors, value];
      }
    }

    setFilters(newFilters);

    // Update URL
    const params = new URLSearchParams(searchParams);
    if (newFilters.tags.length > 0) {
      params.set('tags', newFilters.tags.join(','));
    } else {
      params.delete('tags');
    }
    if (newFilters.colors.length > 0) {
      params.set('colors', newFilters.colors.join(','));
    } else {
      params.delete('colors');
    }

    setSearchParams(params);

    // Trigger refetch
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const removeFilter = (type, value) => {
    toggleFilter(type, value);
  };

  const clearAllFilters = () => {
    setFilters({ tags: [], colors: [] });
    const params = new URLSearchParams(searchParams);
    params.delete('tags');
    params.delete('colors');
    setSearchParams(params);
    
    if (onFiltersChange) {
      onFiltersChange({ tags: [], colors: [] });
    }
  };

  // Get suggested filters from items
  const getSuggestedFilters = () => {
    if (!items || items.length === 0) return { tags: [], colors: [] };

    const tagCounts = {};
    const colorCounts = {};

    items.forEach(item => {
      (item.tags || []).forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
      (item.colors || []).forEach(color => {
        colorCounts[color] = (colorCounts[color] || 0) + 1;
      });
    });

    const topTags = Object.entries(tagCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, Math.floor(maxFilters * 0.6))
      .map(([tag]) => tag);

    const topColors = Object.entries(colorCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, Math.floor(maxFilters * 0.4))
      .map(([color]) => color);

    return { tags: topTags, colors: topColors };
  };

  const suggested = getSuggestedFilters();
  const hasActiveFilters = filters.tags.length > 0 || filters.colors.length > 0;

  if (!hasActiveFilters && suggested.tags.length === 0 && suggested.colors.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm text-neutral-600">Filters:</span>
          {filters.tags.map(tag => (
            <button
              key={`tag-${tag}`}
              onClick={() => removeFilter('tag', tag)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#7A47F5] text-white text-sm rounded-full hover:bg-[#6A37E5] transition-colors"
            >
              <span>{tag}</span>
              <X className="w-3 h-3" />
            </button>
          ))}
          {filters.colors.map(color => (
            <button
              key={`color-${color}`}
              onClick={() => removeFilter('color', color)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#7A47F5] text-white text-sm rounded-full hover:bg-[#6A37E5] transition-colors"
            >
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span>{color}</span>
              <X className="w-3 h-3" />
            </button>
          ))}
          <button
            onClick={clearAllFilters}
            className="text-sm text-neutral-600 hover:text-neutral-900 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Suggested Filters */}
      {!hasActiveFilters && (suggested.tags.length > 0 || suggested.colors.length > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-neutral-600">Filter by:</span>
          {suggested.tags.map(tag => (
            <button
              key={`suggest-tag-${tag}`}
              onClick={() => toggleFilter('tag', tag)}
              className="px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm rounded-full hover:bg-neutral-200 transition-colors"
            >
              {tag}
            </button>
          ))}
          {suggested.colors.map(color => (
            <button
              key={`suggest-color-${color}`}
              onClick={() => toggleFilter('color', color)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm rounded-full hover:bg-neutral-200 transition-colors"
            >
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span>{color}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

